import React from "react";
import { Box, Container, Typography, Chip, Grid } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import EuroIcon from "@mui/icons-material/Euro";
import NearMeIcon from "@mui/icons-material/NearMe";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useLanguage } from "../../contexts/LanguageContext";

const MotionBox = motion(Box);

export interface PriceItem {
  labelKey: string;
  price: string;
  noteKey: string | null;
  highlight?: boolean;
  isSubItem?: boolean;
  isGroupHeader?: boolean;
}

export interface PriceSection {
  id: string;
  number: string;
  titleKey: string;
  subtitleKey: string | null;
  items: PriceItem[];
}

export interface PriceCategory {
  id: string;
  titleKey: string;
  subtitleKey: string;
  image: string;
  features?: string[];
  priceHeader?: string;
  legalNote?: string[];
  sections: PriceSection[];
}

export const PRICE_CATEGORIES: PriceCategory[] = [
  {
    id: "finanzbuchhaltung",
    titleKey: "preise.category_finanz_title",
    subtitleKey: "preise.category_finanz_subtitle",
    image:
      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=900&q=80",
    priceHeader: "Mindesthonorar / Honorar *",
    legalNote: [
      "Die genannten Mindesthonorare / Honorare stellen keine dauerhaft feststehenden Endpreise dar.",
      "Die Höhe und Angemessenheit der Vergütung wird regelmäßig, spätestens jedoch alle sechs Monate, überprüft und gegebenenfalls an den tatsächlichen Bearbeitungsaufwand, die Anzahl der Buchungen sowie die jeweiligen Gegenstandswerte angepasst.",
      "*) Alle Preise verstehen sich gegebenenfalls zuzüglich Auslagenersatz (Kosten) und gesetzlicher Umsatzsteuer.",
      "Wir erbringen unsere Leistungen im Rahmen des § 6 Nr. 3 und 4 StBerG.",
      "Stand: Januar 2026",
    ],
    sections: [
      {
        id: 'finanz_all',
        number: '',
        titleKey: 'preise.finanz_section_title',
        subtitleKey: null,
        items: [
          { labelKey: 'preise.finanz_s1_i1', price: '150,00 € / Monat', noteKey: null },
          { labelKey: 'preise.finanz_s1_i2', price: '250,00 € / Monat', noteKey: null },
          { labelKey: 'preise.finanz_s1_i3', price: '350,00 € / Monat', noteKey: null },
          { labelKey: 'preise.finanz_s1_i4', price: '350,00 € / Monat', noteKey: null },
          { labelKey: 'preise.finanz_s1_i5', price: '450,00 € / Monat', noteKey: null },
          { labelKey: 'preise.finanz_s1_i6', price: '180,00 € / Monat', noteKey: null },
          { labelKey: 'preise.finanz_s1_i7', price: '300,00 € / Quartal', noteKey: null },
          { labelKey: 'preise.finanz_s2_title', price: '110,00 € / Std.', noteKey: null, isGroupHeader: true },
          { labelKey: 'preise.finanz_s2_i1', price: '', noteKey: null, isSubItem: true },
          { labelKey: 'preise.finanz_s2_i2', price: '', noteKey: null, isSubItem: true },
        ],
      },
    ],
  },
  {
    id: "lohnbuchhaltung",
    titleKey: "preise.category_lohn_title",
    subtitleKey: "preise.category_lohn_subtitle",
    image:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=900&q=80",
    legalNote: [
      '*) Alle Preise verstehen sich gegebenenfalls zuzüglich Auslagenersatz (Kosten) und gesetzlicher Umsatzsteuer.',
      'Wir erbringen unsere Leistungen im Rahmen des § 6 Nr. 3 und 4 StBerG.',
    ],
    sections: [
      {
        id: "einrichtung",
        number: "1",
        titleKey: "preise.lohn_s1_title",
        subtitleKey: null,
        items: [
          {
            labelKey: "preise.lohn_s1_i1",
            price: "79,90 €",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s1_i2",
            price: "239,00 €",
            noteKey: "preise.lohn_note_net",
          },
        ],
      },
      {
        id: "abrechnung",
        number: "2",
        titleKey: "preise.lohn_s2_title",
        subtitleKey: "preise.lohn_s2_sub",
        items: [
          {
            labelKey: "preise.lohn_s2_i1",
            price: "20,00 €",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s2_i2",
            price: "19,00 €",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s2_i3",
            price: "17,00 €",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s2_i4",
            price: "27,00 €",
            noteKey: "preise.lohn_note_net",
            highlight: true,
          },
          {
            labelKey: "preise.lohn_s2_i5",
            price: "25,00 €",
            noteKey: "preise.lohn_note_net",
            highlight: true,
          },
          {
            labelKey: "preise.lohn_s2_i6",
            price: "23,00 €",
            noteKey: "preise.lohn_note_net",
            highlight: true,
          },
          {
            labelKey: "preise.lohn_s2_i7",
            price: "20,00 €",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s2_i8",
            price: "12,90 €",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s2_i9",
            price: "19,90 €",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s2_i10",
            price: "5,00 €",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s2_i11",
            price: "15,00 €",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s2_i12",
            price: "40,00 €",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s2_i13",
            price: "9,50 €",
            noteKey: "preise.lohn_note_net",
          },
        ],
      },
      {
        id: "bescheinigungen",
        number: "3",
        titleKey: "preise.lohn_s3_title",
        subtitleKey: null,
        items: [
          {
            labelKey: "preise.lohn_s3_i1",
            price: "32,00 €",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s3_i2",
            price: "32,00 €",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s3_i3",
            price: "9,90 €",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s3_i4",
            price: "49,90 €",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s3_i5",
            price: "49,90 €",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s3_i6",
            price: "65,00 €",
            noteKey: "preise.lohn_note_net",
          },
        ],
      },
      {
        id: "antraege",
        number: "4",
        titleKey: "preise.lohn_s4_title",
        subtitleKey: null,
        items: [
          { labelKey: "preise.lohn_s4_i1", price: "16,50 €", noteKey: null },
          {
            labelKey: "preise.lohn_s4_i2",
            price: "100,00 € / Std.",
            noteKey: "preise.lohn_note_net",
          },
        ],
      },
      {
        id: "nebenleistungen",
        number: "5",
        titleKey: "preise.lohn_s5_title",
        subtitleKey: null,
        items: [
          {
            labelKey: "preise.lohn_s5_i1",
            price: "74,90 €",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s5_i2",
            price: "74,90 €",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s5_i3",
            price: "25,00 € / ¼ Std.",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s5_i4",
            price: "9,90 €",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s5_i5",
            price: "19,90 €",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s5_i6",
            price: "39,90 €",
            noteKey: "preise.lohn_note_net",
          },
        ],
      },
      {
        id: "beratungen",
        number: "6",
        titleKey: "preise.lohn_s6_title",
        subtitleKey: null,
        items: [
          {
            labelKey: "preise.lohn_s6_i1",
            price: "100,00 € / Std.",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s6_i2",
            price: "100,00 € / Std.",
            noteKey: "preise.lohn_note_net",
          },
          {
            labelKey: "preise.lohn_s6_i3",
            price: "100,00 € / Std.",
            noteKey: "preise.lohn_note_net",
          },
        ],
      },
    ],
  },
];

// Legacy exports kept for PriceDetailPage compatibility
export const PRICE_DATA: PriceSection[] = PRICE_CATEGORIES.find(
  (c) => c.id === "lohnbuchhaltung",
)!.sections;
export const SECTION_IMAGES: Record<string, string> = {
  einrichtung:
    "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80",
  abrechnung:
    "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80",
  bescheinigungen:
    "https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=800&q=80",
  antraege:
    "https://images.unsplash.com/photo-1590650153855-d9e808231d41?w=800&q=80",
  nebenleistungen:
    "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
  beratungen:
    "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80",
};

const PreisePage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ bgcolor: "#f8f8f8", borderBottom: "1px solid #e8e8e8" }}>
        <Container
          maxWidth="lg"
          sx={{ pt: { xs: 12, md: 14 }, pb: { xs: 6, md: 8 } }}
        >
          <Chip
            icon={<EuroIcon sx={{ fontSize: "14px !important" }} />}
            label={t("preise.lohn_chip")}
            sx={{
              bgcolor: "#eef0fd",
              color: "#3b52d4",
              fontWeight: 700,
              fontSize: "0.72rem",
              letterSpacing: "0.1em",
              mb: 3,
              border: "1px solid #d0d5f8",
            }}
          />
          <Typography
            variant="overline"
            sx={{
              color: "text.secondary",
              letterSpacing: "3px",
              mb: 2,
              display: "block",
            }}
          >
            {t("nav.preise")}
          </Typography>
          <Typography
            variant="h1"
            sx={{ mb: 3, fontSize: { xs: "2.5rem", md: "3.5rem" } }}
          >
            {t("preise.title")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              maxWidth: 560,
              fontSize: "1.1rem",
              lineHeight: 1.75,
            }}
          >
            {t("preise.subtitle")}
          </Typography>
        </Container>
      </Box>

      {/* ── Category Cards ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={4}>
          {PRICE_CATEGORIES.map((cat, index) => (
            <Grid item xs={12} md={6} key={cat.id}>
              <MotionBox
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                onClick={() =>
                  navigate(`/preise/${cat.id}`, { state: { category: cat } })
                }
                sx={{
                  height: { xs: 420, md: 520 },
                  borderRadius: 3,
                  overflow: "hidden",
                  position: "relative",
                  cursor: "pointer",
                  backgroundImage: `url(${cat.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  transition: "transform 0.35s ease",
                  "&:hover": {
                    transform: "scale(1.02)",
                    "& .card-blur": { opacity: 1 },
                    "& .card-hover": { opacity: 1, transform: "translateY(0)" },
                    "& .card-default": { opacity: 0 },
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(160deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.75) 100%)",
                    zIndex: 1,
                  },
                }}
              >
                {/* Default content */}
                <Box
                  className="card-default"
                  sx={{
                    transition: "opacity 0.3s ease",
                    zIndex: 2,
                    position: "absolute",
                    inset: 0,
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      position: "absolute",
                      top: 20,
                      left: 20,
                      color: "rgba(255,255,255,0.75)",
                      letterSpacing: "2.5px",
                      fontSize: "0.65rem",
                      fontWeight: 600,
                    }}
                  >
                    {t("preise.lohn_chip")}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      position: "absolute",
                      bottom: 56,
                      left: 24,
                      right: 24,
                      color: "white",
                      fontWeight: 800,
                      fontSize: { xs: "1.6rem", md: "2rem" },
                      lineHeight: 1.2,
                      letterSpacing: "-0.5px",
                    }}
                  >
                    {t(cat.titleKey)}
                  </Typography>
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: 26,
                      left: 24,
                      right: 24,
                      color: "rgba(255,255,255,0.65)",
                      fontSize: "0.85rem",
                      lineHeight: 1.5,
                    }}
                  >
                    {t(cat.subtitleKey)}
                  </Typography>
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 24,
                      right: 24,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <NearMeIcon
                      sx={{ fontSize: 14, color: "rgba(255,255,255,0.6)" }}
                    />
                  </Box>
                </Box>

                {/* Blur overlay */}
                <Box
                  className="card-blur"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    backdropFilter: "blur(8px)",
                    bgcolor: "rgba(0,0,0,0.55)",
                    zIndex: 3,
                    opacity: 0,
                    transition: "opacity 0.35s ease",
                  }}
                />

                {/* Hover info */}
                <Box
                  className="card-hover"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 4,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    px: 4,
                    py: 4,
                    opacity: 0,
                    transform: "translateY(12px)",
                    transition: "opacity 0.35s ease, transform 0.35s ease",
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      color: "rgba(255,255,255,0.6)",
                      letterSpacing: "2px",
                      fontSize: "0.65rem",
                      mb: 1.5,
                    }}
                  >
                    {t("preise.lohn_chip")}
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      color: "white",
                      fontWeight: 800,
                      mb: 2,
                      lineHeight: 1.2,
                      letterSpacing: "-0.5px",
                    }}
                  >
                    {t(cat.titleKey)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.75)",
                      lineHeight: 1.7,
                      mb: 3,
                      fontSize: "0.9rem",
                      maxWidth: 320,
                    }}
                  >
                    {t(cat.subtitleKey)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.55)",
                      mb: 3,
                      fontSize: "0.85rem",
                    }}
                  >
                    {cat.sections.length} Leistungsbereiche
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      color: "white",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.8rem",
                        letterSpacing: "1px",
                        textTransform: "uppercase",
                      }}
                    >
                      Preisliste ansehen
                    </Typography>
                    <ArrowForwardIcon sx={{ fontSize: 14 }} />
                  </Box>
                </Box>
              </MotionBox>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default PreisePage;
